import React, { useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import throttle from "lodash/throttle";

// context
import { useSiteHeader } from "core/SiteHeader/context/useSiteHeader.js";

// components
import Loading from "./Loading";

// data
import getMarketingMessage from "core/SiteHeader/context/stateHelpers/getMarketingMessage.js";
import { getHideBellNotification } from "../helpers";

// component
const MountingWrapper = ({ children }) => {
    const {
        animation,
        setHasMounted,
        setHeader,
        setAnimationSequence,
        setAnimationIn,
        setAnimationOut,
        setAnimationOverflow,
        rehydrateState,
        currentPage,
        setHideBell,
        currentUser,
        setMarketingMessageHtml,
        setIsCompact,
        isLoading,
        isLegacyPage
    } = useSiteHeader();

    const showLoadingSpinner = isLegacyPage && isLoading;

    // refs
    const animationSequenceRef = useRef(animation.sequence);
    const lastScrollYRef = useRef(0);
    const scrollDirectionRef = useRef("down");
    const hasMountedRef = useRef(false);
    const animationTimerRef = useRef(null);

    const updateScrollDirection = scrollY => {
        if (scrollY > lastScrollYRef.current) {
            scrollDirectionRef.current = "down";
        } else if (scrollY < lastScrollYRef.current) {
            scrollDirectionRef.current = "up";
        }
        lastScrollYRef.current = scrollY;
    };

    // throttled scrollY side effects
    const throttleScrollCalulations = useCallback(
        throttle((scrollY, animationSequenceRef, scrollDirectionRef) => {
            setAnimationSequence(
                scrollY,
                animationSequenceRef,
                scrollDirectionRef
            );
        }, 100),
        []
    );

    // Has Mounted
    useEffect(() => {
        setHasMounted(true);
    }, []);

    // Legacy :: We do not want header to JUMP once loaded
    // So we added "react-legacy-header-wrapper" ID to hold space until
    // Header mounts and then we set to auto to adjust to scrolling height
    useEffect(() => {
        const rootWrapper = document.getElementById(
            "react-legacy-header-wrapper"
        );
        if (rootWrapper) {
            rootWrapper.style.height = "auto";
        }
    }, []);

    // Evaluate scroll logic
    const evaluateScroll = useCallback(() => {
        const scrollY = window.scrollY;

        updateScrollDirection(scrollY);

        // Defer DOM updates to requestAnimationFrame - hard to tell if this made any difference, and does not seem to hurt either
        // schedule a function to be executed just before the next repaint
        // It ensures your function runs at the most optimal time for visual updates, aligning it with the browser’s refresh rate (typically 60fps, or 16.66ms per frame).
        window.requestAnimationFrame(() => {
            // throttling setHeader (which is bound to a 1:1 relationship with window.scrollY) causes choppy animation
            setHeader(scrollY);
            // Not bound by 1:1 window.scrollY; ok to throttle
            throttleScrollCalulations(
                scrollY,
                animationSequenceRef,
                scrollDirectionRef
            );
        });
    }, []);

    // scroll listener
    useEffect(() => {
        window.addEventListener("scroll", evaluateScroll);
        return () => {
            window.removeEventListener("scroll", evaluateScroll);
        };
    }, []);

    // track hasMounted
    useEffect(() => {
        hasMountedRef.current = true;
    }, []);

    // These items may change client-side without a refresh
    useEffect(() => {
        if (hasMountedRef.current) {
            rehydrateState();
        }
    }, [currentPage]);

    // trigger second row animation
    useEffect(() => {
        clearTimeout(animationTimerRef.current);
        const { animatinInDuration, shouldToggle } = animation.overflow;
        const isAnimationIn = animationSequenceRef.current === "in";

        // Animate in and Out
        isAnimationIn ? setAnimationIn() : setAnimationOut();

        // We only toggle visibiltiy on pages that hide the searchBar on page load
        if (shouldToggle) {
            if (isAnimationIn) {
                animationTimerRef.current = setTimeout(() => {
                    setAnimationOverflow(true);
                    // avoid attempting to clear an expired timeout in older browsers
                    animationTimerRef.current = null;
                }, animatinInDuration);
            } else {
                // ok to hide right away
                setAnimationOverflow(false);
            }
        }
    }, [animationSequenceRef.current]);

    // Cleanup on dismount
    useEffect(() => {
        return () => {
            // Just in case the timer is still running when component dismounts
            if (animationTimerRef.current) {
                clearTimeout(animationTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setHideBell(getHideBellNotification());
    }, []);

    useEffect(() => {
        if (currentUser) {
            const { html } = getMarketingMessage(currentUser);
            setMarketingMessageHtml(html);
        }
    }, [currentUser]);

    useEffect(() => {
        setIsCompact(animation.styleProps.opacity.value === 1);
    }, [animation.styleProps.opacity.value]);

    return showLoadingSpinner ? <Loading /> : <>{children}</>;
};

// prop-types
MountingWrapper.propTypes = {
    children: PropTypes.any
};

export default MountingWrapper;
